import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { bragCardAPI } from '../../services/api';
import ScrollHint from '../../components/ScrollHint';
import './BragCardsListPage.css';

function formatDate(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function BragCardsListPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [player, setPlayer] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/analytics/players/${id}`),
      bragCardAPI.getByPlayer(id),
    ])
      .then(([playerRes, cardsRes]) => {
        setPlayer(playerRes.data);
        setCards(cardsRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const carouselRef = useRef(null);

  useEffect(() => {
    const checkScroll = () => {
      const carousel = carouselRef.current;
      if (!carousel) return;
      const wrapper = carousel.parentElement;
      if (!wrapper) return;
      if (carousel.scrollWidth > carousel.clientWidth) {
        wrapper.classList.add('has-scroll');
      } else {
        wrapper.classList.remove('has-scroll');
      }
    };
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [cards]);

  function handleDeleteClick(cardId) {
    setPendingDeleteId(cardId);
    setShowDeleteConfirm(true);
  }

  async function handleDeleteConfirm() {
    setDeletingId(pendingDeleteId);
    setShowDeleteConfirm(false);
    try {
      await bragCardAPI.delete(pendingDeleteId);
      setCards((prev) => prev.filter((c) => c.id !== pendingDeleteId));
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeletingId(null);
      setPendingDeleteId(null);
    }
  }

  const playerLabel = player
    ? `${player.firstName} ${player.lastName}`
    : '…';

  if (loading) {
    return (
      <>
        <div className="bcl-page">
          <div className="bcl-loading">Loading…</div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="bcl-page">
        <div className="bcl-header">
          <button
            className="bcl-back-btn"
            onClick={() => navigate(`/analytics/players/${id}/dashboard`)}
          >
            ← Dashboard
          </button>
          <div className="bcl-header-text">
            <h1 className="bcl-title">Brag Cards</h1>
            <p className="bcl-subtitle">{playerLabel}</p>
          </div>
          <button
            className="bcl-new-btn"
            onClick={() => navigate(`/brag-card?playerId=${id}`)}
          >
            + New Brag Card
          </button>
        </div>

        {cards.length === 0 ? (
          <div className="bcl-empty">
            <div className="bcl-empty-icon">🏆</div>
            <h2>No saved brag cards yet</h2>
            <p>Create a brag card and click Save to keep it here.</p>
            <button
              className="bcl-new-btn"
              onClick={() => navigate(`/brag-card?playerId=${id}`)}
            >
              + Create First Brag Card
            </button>
          </div>
        ) : (
          <div className="bcl-content">
          <div className="bcl-section">
            <div className="bcl-section-header">
              <span className="bcl-section-title">🏆 Brag Cards</span>
              <span className="bcl-section-count">({cards.length})</span>
            </div>
            <div className="bcl-section-body">
            <div className="graphics-carousel-wrapper">
              <ScrollHint />
              <div className="graphics-carousel" ref={carouselRef}>
              {cards.map((card) => (
                <div key={card.id} className="bcl-card">
                  <div
                    className="bcl-card-preview"
                    style={
                      card.thumbnailImage
                        ? { padding: 0, border: 'none' }
                        : {
                            background: `linear-gradient(160deg, ${card.primaryColor} 0%, ${card.primaryColor}bb 100%)`,
                            borderColor: card.accentColor,
                          }
                    }
                  >
                    {card.thumbnailImage ? (
                      <img
                        src={card.thumbnailImage}
                        alt={card.playerName || 'Brag card'}
                        className="bcl-card-thumbnail"
                      />
                    ) : (
                      <>
                        <div
                          className="bcl-card-accent-bar"
                          style={{ background: card.accentColor }}
                        />
                        <div className="bcl-card-preview-inner">
                          <div className="bcl-card-emoji">
                            {card.milestoneEmoji || '🏆'}
                          </div>
                          <div className="bcl-card-preview-name">
                            {card.playerName || '—'}
                          </div>
                          {card.milestoneLabel && (
                            <div
                              className="bcl-card-preview-milestone"
                              style={{ color: card.accentColor }}
                            >
                              {card.milestoneLabel}
                            </div>
                          )}
                          {card.statLine && (
                            <div className="bcl-card-preview-stat">{card.statLine}</div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="bcl-card-meta">
                    <span className="bcl-card-date">{formatDate(card.createdAt)}</span>
                    <div className="bcl-card-actions">
                      <button
                        className="bcl-btn bcl-btn-edit"
                        onClick={() => navigate(`/brag-card?cardId=${card.id}`)}
                      >
                        Edit →
                      </button>
                      <button
                        className="bcl-btn bcl-btn-delete"
                        onClick={() => handleDeleteClick(card.id)}
                        disabled={deletingId === card.id}
                      >
                        {deletingId === card.id ? '…' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              </div>
            </div>
            </div>
          </div>
          </div>
        )}
      </div>
      {showDeleteConfirm && (
        <div
          className="modal-overlay"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete BragCard?</h3>
            <p>
              This BragCard will be moved to <strong>Recently Deleted</strong>{' '}
              for 3 days.
            </p>
            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
                disabled={deletingId === pendingDeleteId}
              >
                {deletingId === pendingDeleteId ? 'Deleting...' : 'Move to Trash'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
